'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["align", "animate", "children", "className", "onOpenStart", "onOpenComplete", "onCloseStart", "onCloseComplete", "onWheel", "panelClassName", "panelKey", "rootNode", "scrollElementRef", "style", "width", "_dontMakeNewLayer", "containerZIndex"];
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { JUPITER_LAYER } from 'HubStyleTokens/sizes';
import { Provider as PanelComponentRegistrationProvider } from '../context/PanelComponentRegistrationContext';
import * as PanelContext from '../context/PanelContext';
import { constrainVerticalWheelEvent } from '../core/EventHandlers';
import ShareScrollElement from '../decorators/ShareScrollElement';
import UILayer from '../layer/UILayer';
import refObject from '../utils/propTypes/refObject';
import { percentToVW } from '../utils/Styles';
import PanelTransition from './internal/PanelTransition';
import { getPanelTransitionProps } from './internal/panelUtils';
import { DropdownContextComponent } from '../context/DropdownContext';
import { SandboxContext } from '../context/SandboxContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const inPanelContext = {
  inPanel: true
};
const getStyle = (style, computedWidth) => Object.assign({}, style, {
  width: computedWidth,
  outline: 0
});
const getClassName = (align, className, sandboxed) => classNames(className, 'private-panel', sandboxed && 'private-panel--sandboxed', {
  'left': 'private-panel--left',
  'right': 'private-panel--right'
}[align]);
const getPanelClassName = (panelClassName, bodyCount, headerCount) => classNames(panelClassName, 'private-panel__container', bodyCount > 0 && 'private-panel__container--with-body', headerCount > 0 && 'private-panel__container--with-header');
class UIPanel extends Component {
  constructor(props) {
    super(props);
    this.handleWheel = evt => {
      // Allow a custom wheel handler to override this one
      const {
        onWheel,
        scrollElementRef
      } = this.props;
      if (typeof onWheel === 'function') {
        onWheel(evt);
        return;
      }
      constrainVerticalWheelEvent(evt, scrollElementRef.current);
    };
    this.state = {
      bodyCount: 0,
      headerCount: 0
    };
    this._getStyle = memoizeOne(getStyle);
    this._getTransitionProps = memoizeOne(getPanelTransitionProps);
    this._isMounted = true; // Not technically, but child context funcs are called before didMount.

    this._hasHeader = false;
    this._hasBody = false;
    // Track the number of UIPanelHeader and UIPanelBody instances this panel contains. This code
    // should be removed once all headerless/bodyless panels are eliminated.
    this._panelComponentRegistrationContextValue = {
      registerPanelBody: () => {
        this._hasBody = true;
        if (this._isMounted) {
          this.setState(state => ({
            bodyCount: state.bodyCount + 1
          }));
        }
      },
      unregisterPanelBody: () => {
        if (this._isMounted) {
          this.setState(state => ({
            bodyCount: state.bodyCount - 1
          }));
        }
      },
      registerPanelHeader: () => {
        this._hasHeader = true;
        if (this._isMounted) {
          this.setState(state => ({
            headerCount: state.headerCount + 1
          }));
        }
      },
      unregisterPanelHeader: () => {
        if (this._isMounted) {
          this.setState(state => ({
            headerCount: state.headerCount - 1
          }));
        }
      }
    };
  }
  componentDidMount() {
    const {
      scrollElementRef
    } = this.props;

    // Only apply focus if the panel is not sandboxed in UI Library.
    if (!this.context.sandboxed) {
      scrollElementRef.current.focus(); // adding focus so users can tab into panel
    }

    // Using the React `onWheel` event would be restrictive in Chrome (#6272)
    scrollElementRef.current.addEventListener('wheel', this.handleWheel);
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  render() {
    const _this$props = this.props,
      {
        align,
        animate,
        children,
        className,
        onOpenStart,
        onOpenComplete,
        onCloseStart,
        onCloseComplete,
        panelClassName,
        rootNode,
        scrollElementRef,
        style,
        width,
        _dontMakeNewLayer,
        containerZIndex
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      bodyCount,
      headerCount
    } = this.state;
    const {
      sandboxed
    } = this.context;
    let computedWidth = (style || {}).width || width;
    computedWidth = percentToVW(computedWidth);
    const renderedPanel = /*#__PURE__*/_jsx(PanelContext.Provider, {
      value: inPanelContext,
      children: /*#__PURE__*/_jsx("div", Object.assign({}, rest, {
        className: getClassName(align, className, sandboxed),
        ref: scrollElementRef
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        ,
        tabIndex: "0",
        style: this._getStyle(style, computedWidth),
        children: /*#__PURE__*/_jsxs(PanelComponentRegistrationProvider, {
          value: this._panelComponentRegistrationContextValue,
          children: [/*#__PURE__*/_jsx("div", {
            className: getPanelClassName(panelClassName, bodyCount, headerCount),
            children: children
          }), /*#__PURE__*/_jsx(DropdownContextComponent, {
            componentName: UIPanel.displayName
          })]
        })
      }))
    });
    if (_dontMakeNewLayer || sandboxed) {
      return renderedPanel;
    }
    return /*#__PURE__*/_jsx(UILayer, {
      "data-layer-for": "UIPanel",
      rootNode: rootNode,
      Transition: PanelTransition,
      transitionProps: this._getTransitionProps(onOpenStart, onOpenComplete, onCloseStart, onCloseComplete, align, animate, containerZIndex),
      children: renderedPanel
    });
  }
}
UIPanel.propTypes = {
  /**
   * Determines if the sliding animation is shown when the panel opens and closes.
   */
  animate: PropTypes.bool.isRequired,
  /**
   * Determines the side from which the panel enters and exits the page.
   */
  align: PropTypes.oneOf(['left', 'right']).isRequired,
  /**
   * Sets the content that will render within the component, which can include `UIPanelHeader`, `UIPanelBody`, `UIPanelSection`, and `UIPanelFooter`.
   */
  children: PropTypes.node,
  /**
   * Sets the z-index of the panel's parent `UILayer` container.
   */
  containerZIndex: PropTypes.string,
  /**
   * Function that is called when the closing animation of the panel is complete.
   */
  onCloseComplete: PropTypes.func,
  /**
   * Function that is called when the closing animation of the panel is initiated.
   */
  onCloseStart: PropTypes.func,
  /**
   * Function that is called when the opening animation of the panel is complete.
   */
  onOpenComplete: PropTypes.func,
  /**
   * Function that is called when the opening animation of the panel is initiated.
   */
  onOpenStart: PropTypes.func,
  /**
   * A className that is passed to the main container of the panel.
   */
  panelClassName: PropTypes.string,
  /**
   * The DOM node where the panel is rendered, typically on a layer with a higher z-index than other components.
   */
  rootNode: UILayer.propTypes.rootNode,
  /**
   * Ref that is passed down to an underlying div element in the panel.
   */
  scrollElementRef: refObject.isRequired,
  /**
   * Sets a custom width on the panel.
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * When enabled, the panel is not wrapped in `UILayer` and appears on the same portal as the parent layer.
   */
  _dontMakeNewLayer: PropTypes.bool,
  /**
   * Sets an identifier that is used by `UIPanelNavigator` to determine which of the panels should be shown.
   */
  panelKey: PropTypes.string
};
UIPanel.contextType = SandboxContext;
UIPanel.defaultProps = {
  align: 'right',
  animate: true,
  width: '30%',
  containerZIndex: JUPITER_LAYER,
  _dontMakeNewLayer: false
};
UIPanel.displayName = 'UIPanel';
export default ShareScrollElement(UIPanel);